import PropTypes from 'prop-types'
import React from 'react'
import { debounce } from 'lodash'
import { connect } from 'transfer/utils/p2p-connect'
import NewAmountFields from '../../react-components/new-amount-fields'
import ConversionRate from '../../react-components/conversion-rate'
import { amountIsInvalid } from '../../lib/amountUtils'
import * as ViewActions from '../actions/Actions'
import content from 'pp-react-l10n'
import { getAmountParts } from '../utils/currencyUtils'
import {
  getDisclaimerMessage,
  getCurrencyCodeMessage,
} from '../utils/currencyConversionFeeDisclaimer-utils'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { PRODUCT_FLOWS } from '../../../../lib/constants'
import FFFInfoMessage from '../../transfer/components/first-fee-free/info-message'
let i18n = content('crossBorder/calc')

// Export this component for testing purpose:
// http://redux.js.org/docs/recipes/WritingTests.html
export class ForeignExchangeCalculatorComponent extends React.Component {
  static propTypes = {
    // random things
    dispatch: PropTypes.func.isRequired,
    isLoadingExchangeRate: PropTypes.bool,
    isLoadingFees: PropTypes.bool,
    isLoading: PropTypes.bool,
    showFeeDisclaimer: PropTypes.bool.isRequired,
    isPurchase: PropTypes.bool.isRequired,
    isFeeDisclaimerMessageDisabled: PropTypes.bool,
    isReview: PropTypes.bool,
    isRecipientCurrencyRestricted: PropTypes.bool,

    // amount stuff
    destinationAmount: PropTypes.string.isRequired,
    fromCurrencyCode: PropTypes.string.isRequired,
    toCurrencyCode: PropTypes.string.isRequired,
    originAmount: PropTypes.string.isRequired,
    disableAmountField: PropTypes.bool,

    // countries
    toCountryCode: PropTypes.string.isRequired,
    fromCountryCode: PropTypes.string.isRequired,
    toCurrencySymbolMap: PropTypes.object.isRequired,
    userCountry: PropTypes.string.isRequired,

    // fees
    rate: PropTypes.string.isRequired,
    feeAmountBank: PropTypes.string.isRequired,
    feeAmountCard: PropTypes.string.isRequired,
    totalCostBank: PropTypes.string.isRequired,
    totalCostCard: PropTypes.string.isRequired,

    // events
    onFromAmountChange: PropTypes.func,
    onFromCurrencyChange: PropTypes.func,
    onToAmountChange: PropTypes.func,
    onToCurrencyChange: PropTypes.func,

    // absolutely required things
    decimalSeparator: PropTypes.string.isRequired,
    groupSeparator: PropTypes.string.isRequired,
    fromCurrencyCodeList: PropTypes.arrayOf(PropTypes.string).isRequired,
    toCurrencyCodeList: PropTypes.arrayOf(PropTypes.string).isRequired,

    updateCurrencyData: PropTypes.func.isRequired,
    toggleContinueButton: PropTypes.func,
    token: PropTypes.string,

    isFirstFeeFreeEligible: PropTypes.bool,
    showFFFInfoMessage: PropTypes.bool,
    firstFeeFreeThresholdAmount: PropTypes.object,
    enableFFFCapLimit: PropTypes.bool,
  }

  componentDidMount() {
    // debounce these calls so we don't make too many AJAX calls
    const DEBOUNCE_INTERVAL = 300
    this.updateConversion = debounce(this._updateConversion, DEBOUNCE_INTERVAL)
  }

  componentWillUpdate({
    originAmount = '',
    destinationAmount = '',
    isLoading,
  }) {
    const { decimalSeparator } = this.props
    const canContinue =
      !amountIsInvalid(originAmount, decimalSeparator) &&
      !amountIsInvalid(destinationAmount, decimalSeparator) &&
      !isLoading
    if (this.props.toggleContinueButton) {
      this.props.toggleContinueButton(canContinue)
    }
  }

  renderDualAmountFields = () => {
    const {
      decimalSeparator,
      toCurrencyCodeList,
      toCurrencySymbolMap,
      isLoading,
      originAmount,
      fromCurrencyCodeList,
      fromCurrencyCode,
      destinationAmount,
      toCurrencyCode,
    } = this.props
    // parts gives us an array of each individual elements of the formatted amount
    // e.g. [{ type: 'symbol', value: '$' }, { type: 'value', value: '1,000,000.00' }]
    const fromAmountParts = getAmountParts(
      originAmount,
      fromCurrencyCode,
      decimalSeparator
    )
    const toAmountParts = getAmountParts(
      destinationAmount,
      toCurrencyCode,
      decimalSeparator
    )

    return (
      <NewAmountFields
        decimalSeparator={decimalSeparator}
        currencySymbols={toCurrencySymbolMap}
        destinationAmount={destinationAmount}
        fromCurrencyCode={fromCurrencyCode}
        toCurrencyCode={toCurrencyCode}
        toCurrencyCodeList={toCurrencyCodeList}
        fromCurrencyCodeList={fromCurrencyCodeList}
        fromAmountParts={fromAmountParts}
        toAmountParts={toAmountParts}
        originAmount={originAmount}
        onOriginChange={this.handleOriginChange}
        onDestinationChange={this.handleDestinationChange}
        isLoading={isLoading}
        amountOriginText={i18n('label.amountOrigin')}
        amountRecipientText={i18n('label.amountRecipient')}
      />
    )
  }

  renderConversionRate = () => {
    const {
      fromCurrencyCode: fromCurrency,
      toCurrencyCode: toCurrency,
      rate,
      showFeeDisclaimer,
      userCountry,
      isLoadingFees,
      originAmount,
      isReview,
      isFeeDisclaimerMessageDisabled,
      isRecipientCurrencyRestricted,
    } = this.props

    // Get disclaimer message for currency conversion
    const convertWithPayPal = true
    const convertUsingCard = false
    // hard code the value to be true as we don't want to display any fee related content on calculator page
    const isPurchase = true
    const feeDisclaimerMessage = getDisclaimerMessage({
      convertWithPayPal,
      userCountry,
      convertUsingCard,
      isPurchase,
      isReview,
      isFeeDisclaimerMessageDisabled,
    })
    const currencyCodeDisclaimer = getCurrencyCodeMessage(PRODUCT_FLOWS.SEND)

    let componentToRender

    if ((!isLoadingFees && !originAmount.length) || originAmount === '0') {
      componentToRender = (
        <ConversionRate
          exchangeRateText={i18n('label.preExchangeRate')}
          showDisclaimer=""
          disclaimerText=""
          currencyCodeDisclaimer={
            isRecipientCurrencyRestricted ? currencyCodeDisclaimer : ''
          }
        />
      )
    } else if (isLoadingFees) {
      componentToRender = (
        <div
          style={{ width: 18, margin: '20px auto 10px' }}
          className="vx_spinner-small"
        />
      )
    } else {
      componentToRender = (
        <ConversionRate
          exchangeRateText={i18n('label.exchangeRate', {
            fromCurrency,
            toCurrency,
            rate,
          })}
          showDisclaimer={showFeeDisclaimer}
          disclaimerText={feeDisclaimerMessage}
          currencyCodeDisclaimer={
            isRecipientCurrencyRestricted ? currencyCodeDisclaimer : ''
          }
        />
      )
    }

    return <div>{componentToRender}</div>
  }

  renderEstimatedTransactionFee = () => {
    const { feeAmountBank, isLoadingFees } = this.props
    return (
      <div style={{ minHeight: 22 }}>
        {isLoadingFees ? (
          <div
            style={{ width: 18, margin: '20px auto 10px' }}
            className="vx_spinner-small"
          />
        ) : (
          <div style={{ margin: '20px auto', fontWeight: 'bold' }}>
            {!amountIsInvalid(this.props.originAmount)
              ? i18n('estimated.transactionFee', { feeAmountBank })
              : null}
          </div>
        )}
      </div>
    )
  }

  // debounced version created in componentDidMount
  _updateConversion = (data, isThisOriginAmount) => {
    // amount and previousAmount are required.. TODO: validate the object in fetchDestinationAmount

    const {
      dispatch,
      fromCurrencyCode,
      fromCountryCode,
      toCurrencyCode,
      toCountryCode,
      isPurchase,
      token,
    } = this.props

    const fetchConversion = isThisOriginAmount
      ? ViewActions.fetchOriginAmount
      : ViewActions.fetchDestinationAmount

    dispatch(
      fetchConversion({
        // defaults
        fromCurrencyCode,
        fromCountryCode,
        toCurrencyCode,
        toCountryCode,
        isPurchase,
        token,
        // overrides
        ...data,
      })
    )
  }

  /**
   * Handle amount origin changes
   * @param {Event} event The event being fired
   */
  handleFromAmountChange = event => {
    const amount = event.target.value
    // const isInitialAmountEntry = !this.props.hasEnteredOriginAmount
    const { fromCurrencyCode, isPurchase, originAmount, dispatch } = this.props

    dispatch(
      ViewActions.changeOriginAmount({
        amount,
        previousAmount: originAmount,
        isInitialAmountEntry: false,
        fromCurrencyCode,
        isPurchase,
      })
    )

    // get the destination amount based on these few properties
    this.updateConversion(
      {
        amount,
        previousAmount: originAmount,
        isPurchase,
      },
      false
    )
  }

  /**
   * Handle amount destination changes
   * @param {Event} event The event being fired
   */
  handleToAmountChange = ({ target: { value: amount } }) => {
    // const isInitialAmountEntry = !this.props.hasEnteredDestinationAmount
    const {
      toCurrencyCode,
      isPurchase,
      dispatch,
      destinationAmount,
    } = this.props
    const { changeAmount, changeDestinationAmount } = ViewActions

    dispatch(
      changeDestinationAmount({
        toCurrencyCode,
        amount,
        isInitialAmountEntry: false,
        previousAmount: destinationAmount,
        isPurchase,
      })
    )

    // calc the origin amount
    this.updateConversion(
      {
        amount,
        toCurrencyCode,
        previousAmount: destinationAmount,
        isPurchase,
      },
      true
    )

    dispatch(changeAmount(amount))
  }

  /**
   * @param {String} toAmount the new amount
   * @param {String} toCurrencyCode the new currency code
   */
  handleDestinationChange = (
    toAmount: string,
    toCurrencyCode: string = this.props.toCurrencyCode
  ) => {
    const {
      dispatch,
      destinationAmount,
      fromCurrencyCode,
      updateCurrencyData,
      disableAmountField,
    } = this.props

    if (disableAmountField) {
      return
    }

    const {
      changeDestinationCurrencyCode,
      changeDestinationAmount,
    } = ViewActions

    const currencyTypeChanged = toCurrencyCode !== this.props.toCurrencyCode
    if (currencyTypeChanged) {
      tracking(
        Events.p2p_amount_currency_selection_pressed({
          party: 'receiver',
          updated_currency: toCurrencyCode,
        })
      )
    }
    dispatch(
      changeDestinationCurrencyCode(toCurrencyCode, {
        shouldFireAnalytics: currencyTypeChanged,
      })
    )

    dispatch(
      changeDestinationAmount({
        toCurrencyCode,
        amount: toAmount,
        isInitialAmountEntry: false,
        previousAmount: destinationAmount,
      })
    )

    // only fetch origin amount if it is cross currency
    // otherwise we do not show calculator and thus no need to fetch origin amount
    if (fromCurrencyCode !== toCurrencyCode) {
      this.updateConversion(
        {
          amount: toAmount,
          toCurrencyCode,
          previousCurrencyCode: this.props.toCurrencyCode,
          previousAmount: destinationAmount,
          currencyChanged: true,
        },
        true
      )
    }

    updateCurrencyData(toCurrencyCode)
  }

  /**
   * @param {String} fromAmount the new amount
   * @param {String} fromCurrencyCode the new currency code
   */
  handleOriginChange = (
    fromAmount: string,
    fromCurrencyCode: string = this.props.fromCurrencyCode
  ) => {
    const {
      dispatch,
      originAmount,
      toCurrencyCode,
      updateCurrencyData,
      disableAmountField,
      firstFeeFreeThresholdAmount,
    } = this.props

    if (disableAmountField) {
      return
    }

    const { changeOriginCurrencyCode, changeOriginAmount } = ViewActions

    const currencyTypeChanged = fromCurrencyCode !== this.props.fromCurrencyCode
    if (currencyTypeChanged) {
      tracking(
        Events.p2p_amount_currency_selection_pressed({
          party: 'sender',
          updated_currency: fromCurrencyCode,
        })
      )
    }
    dispatch(
      changeOriginCurrencyCode({
        fromCurrencyCode,
        amount: fromAmount,
        shouldFireAnalytics: currencyTypeChanged,
      })
    )
    dispatch(
      changeOriginAmount({
        fromCurrencyCode,
        amount: fromAmount,
        isInitialAmountEntry: false,
        previousAmount: originAmount,
      })
    )
    // only fetch destination amount if it is cross currency
    // otherwise we do not show calculator and thus no need to fetch destination amount
    if (toCurrencyCode !== fromCurrencyCode) {
      this.updateConversion(
        {
          amount: fromAmount,
          fromCurrencyCode,
          previousCurrencyCode: fromCurrencyCode,
          currencyChanged: true,
        },
        false
      )
    }

    if (
      this.props.isFirstFeeFreeEligible &&
      this.props.enableFFFCapLimit &&
      this.props.toCountryCode === 'US'
    ) {
      if (
        this.props.showFFFInfoMessage &&
        Number(fromAmount) <= Number(firstFeeFreeThresholdAmount.value)
      ) {
        this.props.dispatch(ViewActions.toggleFFFInfoMessage(false))
      }

      if (
        !this.props.showFFFInfoMessage &&
        Number(fromAmount) > Number(firstFeeFreeThresholdAmount.value)
      ) {
        this.props.dispatch(ViewActions.toggleFFFInfoMessage(true))
      }
    }

    updateCurrencyData(toCurrencyCode)
  }

  render() {
    const { isLoadingExchangeRate, showFFFInfoMessage } = this.props
    return (
      <div style={{ marginBottom: 28 }}>
        {this.renderDualAmountFields()}
        {showFFFInfoMessage && <FFFInfoMessage />}
        <div style={{ minHeight: 22 }}>
          {isLoadingExchangeRate ? (
            <div
              style={{ width: 18, margin: '0 auto 10px' }}
              className="vx_spinner-small"
            />
          ) : (
            this.renderConversionRate()
          )}
        </div>
      </div>
    )
  }
}

// make props from each property in the cross border reducer
const stateToProps = state => ({
  showFeeDisclaimer: state.crossBorder.disclaimerCountries.includes(
    state.crossBorder.toCountryCode
  ),
  isLoading: state.crossBorder.isLoadingDestinationAmount,
  isLoadingExchangeRate: state.crossBorder.isLoadingExchangeRate,
  isLoadingFees: state.crossBorder.isLoadingFees,
  ...state.crossBorder,
  userCountry: state.user.country,
  isPurchase: state.transaction.isPurchase,
  isFeeDisclaimerMessageDisabled: state.crossBorder.disableFeeDisclaimerMessage,
  token: state.recipient.token,

  isFirstFeeFreeEligible: state.transaction.isFirstFeeFreeEligible,
  firstFeeFreeThresholdAmount: state.transaction.firstFeeFreeThresholdAmount,
  showFFFInfoMessage: state.transaction.showFFFInfoMessage,
  enableFFFCapLimit: state.transaction.enableFFFCapLimit,
})

const dispatchToProps = dispatch => ({
  updateCurrencyData: currencyCode =>
    dispatch(ViewActions.changeCurrency(currencyCode)),
  dispatch,
})
export default connect(
  stateToProps,
  dispatchToProps
)(ForeignExchangeCalculatorComponent)
